import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Button,
  Box,
  Tooltip,
} from '@material-ui/core';
import { Autorenew, Visibility } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import EditCashBoxContext from 'contexts/EditCashBoxContext';
import { parseDateTime } from 'utils/datetime';
import { formatMoneyAuto } from 'utils/formatMoney';
import PaginationData from 'types/models/PaginationData';
import { ConsumptionReportPayloadData } from 'types/forms/ConsumptionsReportFormProps';
import ConsumptionsReportForm from 'forms/ConsumptionsReportForm';
import ConfirmDialog from 'components/ConfirmDialog';
import 'styles/pages/BillingOrdersPage.scss';
import InfoBanner from 'components/InfoBanner';
import { getCashBoxes, getCashBoxesReports, reOpenCashBox } from 'services/cloverServices';
import CashBox from 'types/models/CashBox';

export default (): JSX.Element => {
  const history = useHistory();
  const { selectedCashBox, setSelectedCashBox } = React.useContext(EditCashBoxContext);
  const [currentFormData, setCurrentFormData] = React.useState<ConsumptionReportPayloadData | null>(null);
  const [paginationCashBoxes, setPaginationCashBoxes] = React.useState<PaginationData<CashBox> | null>(null);
  const paymentIdsForSearch = React.useRef<string[]>([]);
  const [bannerMessage, setBannerMessage] = React.useState('');
  const [infoBannerResult, setInfoBannerResult] = React.useState<boolean>(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const { t, i18n } = useTranslation();

  const loadData = async (page?: number) => {
    if (!currentFormData) return;
    const { cloverPointIds, ...consumptionsReportPayload } = currentFormData;

    const getPaginationOrders = await getCashBoxes({ ...consumptionsReportPayload, cloverPointIds }, page);
    if (getPaginationOrders) {
      setPaginationCashBoxes(getPaginationOrders);
    }
  };

  React.useEffect(() => {
    if (paginationCashBoxes) {
      setPaginationCashBoxes(null);
    }
    if (paymentIdsForSearch.current.length > 0) {
      paymentIdsForSearch.current = [];
    }
    loadData();
  }, [currentFormData]);

  const downloadReport = async (): Promise<void> => {
    if (!currentFormData) return;
    const { cloverPointIds, ...consumptionsReportPayload } = currentFormData;

    const result = await getCashBoxesReports({
      formData: { ...consumptionsReportPayload, cloverPointIds },
      lang: i18n.language,
    });
    if (result) {
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(
        new Blob([result], {
          type: 'application/octet-stream',
        }),
      );
      a.download = 'reporte_cajas.xlsx';
      a.click();
    }
  };

  const onReOpenCashbox = async (): Promise<void> => {
    try {
      if (!selectedCashBox) return;
      const result = await reOpenCashBox(selectedCashBox.id);
      if (result) {
        setInfoBannerResult(true);
        setBannerMessage(t('cash-box-re-opened-successfully'));
        loadData();
      } else {
        setInfoBannerResult(false);
        setBannerMessage(t('cash-box-re-opened-error'));
      }
    } catch (error) {}
  };

  const renderShowButton = (cashBox: CashBox): JSX.Element => {
    return (
      <Tooltip title={t('see-details').toString()} arrow>
        <IconButton
          color="inherit"
          onClick={() => {
            setSelectedCashBox(cashBox);
            history.push('cash-boxes/transactions');
          }}
        >
          <Visibility />
        </IconButton>
      </Tooltip>
    );
  };

  const renderReOpenButton = (cashBox: CashBox): JSX.Element => {
    return (
      <Tooltip title={t('cash-boxes-re-open').toString()} arrow>
        <IconButton
          color="inherit"
          onClick={() => {
            setSelectedCashBox(cashBox);
            setOpenDialog(true);
          }}
        >
          <Autorenew />
        </IconButton>
      </Tooltip>
    );
  };

  const renderContent = (cashBox: CashBox, index: number): JSX.Element => {
    return (
      <React.Fragment key={index}>
        <TableRow>
          <TableCell component="th" scope="row">
            {cashBox.id}
          </TableCell>
          <TableCell>{parseDateTime(cashBox.createdAt)}</TableCell>
          <TableCell>{cashBox.closedAt ? parseDateTime(cashBox.closedAt) : '-'}</TableCell>
          <TableCell>{formatMoneyAuto(cashBox.cashBalance)}</TableCell>
          <TableCell>{formatMoneyAuto(cashBox.qrBalance)}</TableCell>
          <TableCell>{formatMoneyAuto(cashBox.cardBalance)}</TableCell>
          <TableCell>
            {renderShowButton(cashBox)}
            {cashBox.closedAt && renderReOpenButton(cashBox)}
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onReOpenCashbox}
        onCancel={() => setOpenDialog(false)}
      />
      <ConsumptionsReportForm
        isForCashBoxes={true}
        currentFormData={currentFormData}
        setCurrentFormData={setCurrentFormData}
      />
      <>
        <InfoBanner
          message={bannerMessage}
          result={infoBannerResult}
          onClose={() => {
            setInfoBannerResult(false);
            setBannerMessage('');
          }}
        />
        <TableContainer className="table-container" component={Paper}>
          <Table>
            <TableHead className="table-header">
              <TableRow>
                <TableCell>{t('schema-cash-boxes-report-id')}</TableCell>
                <TableCell>{t('schema-cash-boxes-report-created-date')}</TableCell>
                <TableCell>{t('schema-cash-boxes-report-closed-date')}</TableCell>
                <TableCell>{t('schema-cash-boxes-report-cash-balance')}</TableCell>
                <TableCell>{t('schema-cash-boxes-report-qr-balance')}</TableCell>
                <TableCell>{t('schema-cash-boxes-report-card-balance')}</TableCell>
                <TableCell>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginationCashBoxes?.data && paginationCashBoxes?.data.length > 0 ? (
                paginationCashBoxes?.data.map((cashBox: CashBox, index) => renderContent(cashBox, index))
              ) : (
                <TableRow>
                  <TableCell>{t('consumptionsReport-page-noOperations')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginationCashBoxes && (
          <>
            <TablePagination
              component="div"
              count={paginationCashBoxes.total}
              page={paginationCashBoxes.currentPage - 1}
              onPageChange={(_, page: number) => {
                loadData(page + 1);
              }}
              rowsPerPage={paginationCashBoxes.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />

            {paginationCashBoxes?.data.length > 0 && (
              <Box marginTop="1rem">
                <Button
                  className="btn-50"
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={() => {
                    downloadReport();
                  }}
                >
                  {t('consumptionsReport-page-download')}
                </Button>
              </Box>
            )}
          </>
        )}
      </>
    </>
  );
};
